import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { LogoHorizontal } from '../components/Logo';
import { TextInput, PasswordInput } from '../components/Input';
import { PrimaryButton } from '../components/Button';
import { FormHeader, Divider } from '../components/Card';
import api, { formatApiError } from '../lib/api';

export default function Register() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const familyCode = params.get('code') || '';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [terms, setTerms] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (password !== confirm) return setError('Las contraseñas no coinciden');
        if (password.length < 6) return setError('La contraseña debe tener al menos 6 caracteres');
        if (!terms) return setError('Debes aceptar los términos y condiciones');

        setLoading(true);
        try {
            await api.post('/auth/register', { email: email.trim().toLowerCase(), password, family_code: familyCode || undefined });
            navigate(`/verificar-email?email=${encodeURIComponent(email.trim().toLowerCase())}`);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-surface app-shell flex flex-col py-8 px-6">
            <div className="flex justify-center mb-6 fade-up">
                <LogoHorizontal size="lg" />
            </div>

            <div className="bg-white rounded-[24px] shadow-card p-6 fade-up fade-up-delay-1" data-testid="register-card">
                <FormHeader title="Crear Cuenta" subtitle="Únete a miles de familias organizadas" />

                <div className="grid grid-cols-2 gap-3 mb-1">
                    <button
                        disabled
                        title="Pronto disponible"
                        className="h-12 bg-white border border-gray-200 rounded-2xl flex items-center justify-center gap-2 text-gray-400 text-sm font-semibold cursor-not-allowed opacity-60"
                        data-testid="register-google-btn"
                    >
                        <GoogleIcon /> Google
                    </button>
                    <button
                        disabled
                        title="Disponible en versión nativa"
                        className="h-12 bg-white border border-gray-200 rounded-2xl flex items-center justify-center gap-2 text-gray-400 text-sm font-semibold cursor-not-allowed opacity-60"
                        data-testid="register-apple-btn"
                    >
                        <AppleIcon /> Apple
                    </button>
                </div>
                <p className="text-center text-[11px] text-gray-400 mt-1.5">Próximamente</p>

                <Divider text="O regístrate con tu correo" />

                <form onSubmit={submit} className="space-y-3">
                    <TextInput
                        label="Correo electrónico"
                        type="email"
                        icon={Mail}
                        placeholder="tu@correo.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        testId="register-email-input"
                    />
                    <PasswordInput
                        label="Contraseña"
                        icon={Lock}
                        placeholder="Mínimo 6 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        testId="register-password-input"
                    />
                    <PasswordInput
                        label="Confirmar contraseña"
                        icon={Lock}
                        placeholder="Repite tu contraseña"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                        testId="register-confirm-input"
                    />

                    <label className="flex items-start gap-2 cursor-pointer pt-1" data-testid="register-terms-checkbox">
                        <input
                            type="checkbox"
                            checked={terms}
                            onChange={(e) => setTerms(e.target.checked)}
                            className="mt-1 w-5 h-5 accent-brand cursor-pointer"
                        />
                        <span className="text-xs text-gray-600 leading-snug">
                            Acepto los <span className="text-brand font-semibold">términos y condiciones</span> y la <span className="text-brand font-semibold">política de privacidad</span>.
                        </span>
                    </label>

                    {error && <div className="text-red-500 text-sm font-medium" data-testid="register-error">{error}</div>}

                    {familyCode && (
                        <div className="bg-brand/5 border border-brand/20 rounded-2xl p-3 text-xs text-brand-navy text-center" data-testid="register-family-code-info">
                            Te unirás a la familia con código <span className="font-bold">{familyCode}</span> tras verificar tu correo.
                        </div>
                    )}

                    <PrimaryButton type="submit" loading={loading} testId="register-submit-btn" className="mt-2">
                        Crear Cuenta
                    </PrimaryButton>
                </form>

                <div className="text-center mt-4 text-sm text-gray-500">
                    ¿Ya tienes cuenta?{' '}
                    <button onClick={() => navigate(familyCode ? `/login?code=${familyCode}` : '/login')} className="text-brand-navy font-bold hover:underline" data-testid="register-login-link">
                        Inicia sesión
                    </button>
                </div>
            </div>
        </div>
    );
}

function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
    );
}
function AppleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
    );
}
